import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import FillLaterAddressField from 'common/components/FillLaterAddressField';
import useRecipientAddresses from 'common/hooks/useRecipientAddresses';
import formatDrugLabel from 'common/utils/formatDrugLabel';
import NewPatientAddressButton from 'modules/PatientView/components/ShippingTab/NewPatientAddressButton';
import { getPatient } from 'store/thunks/patientThunks';
import { updateFillLaterAddress } from 'store/thunks/workQueueThunks';

import { FILL_LATER_TYPE_LABEL } from '../constants/fillLaterEligibility';
import { formatScheduledDate } from '../utils/fillLaterDates';

const iconCircleSx = (color) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: color,
  flexShrink: 0,
});

const bandLabelSx = {
  fontSize: '0.62rem',
  color: 'rgba(255,255,255,0.55)',
  textTransform: 'uppercase',
  letterSpacing: 0.8,
};

const bandValueSx = { fontSize: '0.9rem', fontWeight: 700, color: '#fff' };

const cellLabelSx = {
  fontSize: '0.62rem',
  color: 'text.disabled',
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  fontWeight: 600,
};

const UpdateFillLaterAddressDialog = ({ open, onClose, item }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const mpi = item.patientId;
  const patientLoaded = useSelector(({ patient }) => !!patient?.[mpi]?.firstName);
  const storedAddressCount = useSelector(({ patient }) => patient?.[mpi]?.addresses?.length ?? 0);
  const { addresses, loading, reload } = useRecipientAddresses({
    orderId: item.referenceOrderId,
    enabled: open,
  });

  const [selected, setSelected] = useState(item.addressDirectoryId ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [manuallyVerified, setManuallyVerified] = useState(false);

  const isWholeOrder = item.fillLaterType === 'SOURCE_BY_ORDER';

  useEffect(() => {
    if (!open) return;

    setSelected(item.addressDirectoryId ?? null);

    if (!patientLoaded) dispatch(getPatient({ mpi }));
  }, [open, mpi, item.addressDirectoryId, patientLoaded, dispatch]);

  useEffect(() => {
    if (open && storedAddressCount) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedAddressCount]);

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const updated = await dispatch(
        updateFillLaterAddress({
          fillLaterItemId: item.fillLaterItemId,
          addressDirectoryId: selected,
          isManuallyVerified: manuallyVerified,
        })
      );
      if (!updated) return;
      enqueueSnackbar(
        selected ? 'Shipping address updated' : 'Now shipping to the original order address',
        { variant: 'success' }
      );
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const unchanged = (selected ?? null) === (item.addressDirectoryId ?? null);

  const cells = [
    { label: 'Drug', value: formatDrugLabel(item), Icon: MedicationOutlinedIcon, wrap: true },
    {
      label: 'Scheduled for',
      value: formatScheduledDate(item.scheduledFor),
      Icon: EventOutlinedIcon,
    },
    { label: 'Quantity', value: item.splitQuantity, Icon: Inventory2OutlinedIcon },
    { label: 'Partner', value: item.partnerName, Icon: BusinessOutlinedIcon },
    { label: 'Practice', value: item.practiceName, Icon: StorefrontOutlinedIcon },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 600 }}>
            Change shipping address
          </Typography>
          <IconButton size='small' onClick={onClose} disabled={submitting}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 1.5, bgcolor: '#1a3a6b' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, px: 2.5, py: 1.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={iconCircleSx('rgba(255,255,255,0.12)')}>
                <Inventory2OutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.9)' }} />
              </Box>
              <Box>
                <Typography sx={bandLabelSx}>Order</Typography>
                <Typography sx={{ ...bandValueSx, fontFamily: 'monospace', letterSpacing: 0.5 }}>
                  {item.referenceOrderId}
                </Typography>
              </Box>
            </Box>

            <Divider
              orientation='vertical'
              flexItem
              sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: 0.5 }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
              <Box sx={iconCircleSx('rgba(255,255,255,0.12)')}>
                <PersonOutlineIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.9)' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={bandLabelSx}>Recipient</Typography>
                <Typography sx={bandValueSx} noWrap>
                  {item.patientName}
                </Typography>
              </Box>
            </Box>

            <Chip
              label={FILL_LATER_TYPE_LABEL[item.fillLaterType] ?? 'Fill later'}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 26,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.3)',
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            mb: 2,
            overflow: 'hidden',
          }}
        >
          {cells.map(({ label, value, Icon, wrap }, index) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                minWidth: 0,
                // Three to the first row, two to the second, so the rules follow the grid rather
                // than the array: a left edge on everything except the first of its row, a top
                // edge on every row after the first.
                gridColumn: index < 3 ? 'span 2' : 'span 3',
                ...(index !== 0 &&
                  index !== 3 && {
                    borderLeft: '1px solid',
                    borderLeftColor: 'divider',
                  }),
                ...(index > 2 && { borderTop: '1px solid', borderTopColor: 'divider' }),
              }}
            >
              <Box sx={{ ...iconCircleSx('#e8ecf5'), width: 30, height: 30 }}>
                <Icon sx={{ fontSize: 15, color: '#031c57' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={cellLabelSx}>{label}</Typography>
                <Typography
                  sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.25 }}
                  noWrap={!wrap}
                  title={value}
                >
                  {value || '—'}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {isWholeOrder && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            This fill is part of a whole-order move. Changing the address changes it for every item
            still waiting on that order — the order can only ship to one place.
          </Alert>
        )}

        <Box
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 2, py: 1.75 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              mb: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <Box sx={{ ...iconCircleSx('#e8ecf5'), width: 30, height: 30 }}>
                <LocalShippingOutlinedIcon sx={{ fontSize: 15, color: '#031c57' }} />
              </Box>
              <Typography sx={cellLabelSx}>Ship this fill to</Typography>
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                '& .MuiButton-root': {
                  py: 0.3,
                  px: 1.25,
                  minWidth: 0,
                  fontSize: '0.72rem',
                  boxShadow: 'none',
                },
              }}
            >
              <NewPatientAddressButton mpi={mpi} showDeliveryInstructions={false}>
                Add a new address
              </NewPatientAddressButton>
            </Box>
          </Box>

          <FillLaterAddressField
            id='UpdateFillLaterAddressDialog-address'
            addresses={addresses}
            value={selected}
            onChange={setSelected}
            onBlockedChange={setBlocked}
            onAcknowledgedChange={setManuallyVerified}
            inheritedAddress={item.referenceAddress ?? item.address}
            currentValue={item.addressDirectoryId ?? null}
            currentAddress={item.address}
            loading={loading}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant='caption' color='text.secondary' sx={{ flex: 1 }}>
          {unchanged ? 'No changes yet' : 'The address can be changed until the fill is created.'}
        </Typography>
        <Button variant='outlined' color='secondary' onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          variant='contained'
          color='primary'
          loading={submitting}
          disabled={unchanged || loading || blocked}
          onClick={handleSubmit}
        >
          Save address
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

UpdateFillLaterAddressDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    fillLaterItemId: PropTypes.string.isRequired,
    referenceOrderId: PropTypes.string,
    patientId: PropTypes.string,
    patientName: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
    splitQuantity: PropTypes.string,
    scheduledFor: PropTypes.string,
    fillLaterType: PropTypes.string,
    partnerName: PropTypes.string,
    practiceName: PropTypes.string,
    addressDirectoryId: PropTypes.string,
    address: PropTypes.shape({}),
    referenceAddress: PropTypes.shape({}),
  }).isRequired,
};

export default UpdateFillLaterAddressDialog;
